import { makeStyles, Theme } from '@material-ui/core/styles';

/**
 * Shared styles for trend components (Standard Compliance Trend, Technical Debt Trend).
 * Extracted to reduce code duplication between StandardComplianceTrendComponent and TechnicalDebtTrendComponent.
 */
export const useTrendStyles = makeStyles<Theme>((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  sectionContainer: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    background: theme.palette.background.paper,
    borderRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.palette.type === 'dark'
      ? '0 4px 12px rgba(0,0,0,0.3)'
      : '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: theme.palette.type === 'dark'
        ? '0 6px 16px rgba(0,0,0,0.4)'
        : '0 4px 12px rgba(0,0,0,0.12)',
    },
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.5rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  dateRangeContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  dateField: {
    minWidth: '200px',
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
    },
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: theme.palette.type === 'dark' ? 'invert(1)' : 'invert(0)',
      cursor: 'pointer',
    },
    '& input[type="date"]::-webkit-inner-spin-button': {
      filter: theme.palette.type === 'dark' ? 'invert(1)' : 'invert(0)',
    },
  },
  chartContainer: {
    marginTop: theme.spacing(2),
    height: '350px',
    borderRadius: '6px',
    padding: `${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(2)}`,
    position: 'relative',
    overflow: 'visible',
  },
  chartCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, #424242 0%, #303030 100%)'
      : 'linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)',
    borderRadius: '8px',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : `1px solid ${theme.palette.divider}`,
    boxShadow: theme.palette.type === 'dark'
      ? '0 4px 12px rgba(0,0,0,0.3)'
      : '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.palette.type === 'dark'
        ? '0 8px 20px rgba(0,0,0,0.4)'
        : '0 4px 12px rgba(0,0,0,0.12)',
      transform: 'translateY(-4px)',
    },
    '& .MuiCardContent-root': {
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, #424242 0%, #303030 100%)'
        : 'linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)',
      padding: theme.spacing(2),
      '&:last-child': {
        paddingBottom: theme.spacing(1),
      },
    },
  },
  chartTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chartTitleSmall: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  metricCard: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, #424242 0%, #303030 100%)'
      : 'linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)',
    borderRadius: '8px',
    padding: theme.spacing(2),
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : `1px solid ${theme.palette.divider}`,
    height: '100%',
    boxShadow: theme.palette.type === 'dark'
      ? '0 4px 12px rgba(0,0,0,0.3)'
      : '0 2px 8px rgba(0,0,0,0.08)',
  },
  metricValue: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    lineHeight: 1,
    textAlign: 'center',
  },
  metricLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    textAlign: 'center',
    lineHeight: 1.2,
  },
  subsectionContainer: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    background: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.02)'
      : 'rgba(0, 0, 0, 0.01)',
    borderRadius: '6px',
    border: `1px solid ${theme.palette.divider}`,
  },
  trendIndicator: {
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.5),
  },
  trendUp: {
    color: theme.palette.error.main,
  },
  trendDown: {
    color: theme.palette.success.main,
  },
  trendStable: {
    color: theme.palette.text.secondary,
  },
}));
